import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import type {
  OrgInvitationRole,
  OrgInvitationSummary,
  OrgMemberSummary,
} from '@nadoutong/contracts';
import { PlatformApiError } from '../../api/client.ts';
import {
  useCreateOrgInvitation,
  useOrgInvitations,
  useOrgMembers,
  useRevokeOrgInvitation,
} from '../../api/useOrgs.ts';
import { Modal } from '../../components/Modal.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { StatusChip } from '../../components/StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { formatTimestamp } from '../../components/formatters.ts';
import { useClipboard } from '../../hooks/useClipboard.tsx';
import { hasPermission, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, FormField, Input, Panel, TBody, Table, Td, Th, THead, Tr, cx } from '../../ui/index.ts';
import { MemberModelPermissionsModal } from './MemberModelPermissionsModal.tsx';

/**
 * Org members card for `/settings` — Field Notes rebuild (epic #950
 * PR-4) of the console-shared Settings factory's members section:
 * member roster, pending invitations (invite / revoke), per-member
 * model-permission editor entry.
 */

type TranslateFn = ReturnType<typeof useT>;

function memberRoleLabel(t: TranslateFn, role: OrgMemberSummary['role']): string {
  const key: I18nKey =
    role === 'owner'
      ? 'settings.members.role.owner'
      : role === 'admin'
        ? 'settings.members.role.admin'
        : 'settings.members.role.member';
  return t(key);
}

function invitedRoleLabel(t: TranslateFn, role: OrgInvitationRole): string {
  return role === 'admin'
    ? t('settings.invite_modal.role.admin')
    : t('settings.invite_modal.role.member');
}

function isOrgInvitationRole(value: string): value is OrgInvitationRole {
  return value === 'admin' || value === 'member';
}

export function MembersCard(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  const orgId = user?.orgId ?? undefined;
  const membersQ = useOrgMembers(orgId);
  const invitationsQ = useOrgInvitations(orgId);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [modelPermissionsMember, setModelPermissionsMember] = useState<OrgMemberSummary | null>(
    null,
  );

  const canInvite = hasPermission(user, 'org:invitations:manage');
  const canManageModelPermissions = hasPermission(user, 'org:members:manage');

  return (
    <Panel
      caption={t('settings.members.title')}
      padded
      testId="settings-members-card"
      actions={
        canInvite ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setInviteOpen(true)}
            data-testid="members-invite-button"
          >
            <UserPlus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('settings.members.invite_button')}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-5">
        <p className="m-0 text-caption text-ink-low">{t('settings.members.subtitle')}</p>

        {membersQ.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="members-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
            <span className="sr-only">{t('settings.members.loading')}</span>
          </div>
        ) : membersQ.isError ? (
          <p className="m-0 text-body text-bad" role="alert" data-testid="members-error">
            {membersQ.error instanceof PlatformApiError
              ? `${membersQ.error.code}: ${membersQ.error.message}`
              : t('settings.members.error')}
          </p>
        ) : membersQ.data !== undefined ? (
          <MembersTable
            members={membersQ.data.members}
            canManageModelPermissions={canManageModelPermissions}
            onEditModelPermissions={(member) => setModelPermissionsMember(member)}
          />
        ) : null}

        {canInvite ? <InvitationsSection orgId={orgId} query={invitationsQ} /> : null}
      </div>

      {inviteOpen && orgId !== undefined ? (
        <InviteMemberModal
          orgId={orgId}
          onClose={() => setInviteOpen(false)}
          onCreated={(url) => {
            setInviteOpen(false);
            setLastInviteUrl(url);
          }}
        />
      ) : null}

      {lastInviteUrl !== null ? (
        <InviteLinkModal url={lastInviteUrl} onClose={() => setLastInviteUrl(null)} />
      ) : null}

      {modelPermissionsMember !== null && orgId !== undefined ? (
        <MemberModelPermissionsModal
          orgId={orgId}
          member={modelPermissionsMember}
          onClose={() => setModelPermissionsMember(null)}
        />
      ) : null}
    </Panel>
  );
}

interface MembersTableProps {
  readonly members: readonly OrgMemberSummary[];
  readonly canManageModelPermissions: boolean;
  readonly onEditModelPermissions: (member: OrgMemberSummary) => void;
}

function MembersTable({
  members,
  canManageModelPermissions,
  onEditModelPermissions,
}: MembersTableProps): JSX.Element {
  const t = useT();
  if (members.length === 0) {
    return (
      <p className="m-0 text-body text-ink-low" data-testid="members-empty">
        {t('settings.members.empty')}
      </p>
    );
  }
  return (
    <div data-testid="members-table">
      <Table>
        <THead>
          <Tr interactive={false}>
            <Th>{t('settings.members.column.email')}</Th>
            <Th>{t('settings.members.column.role')}</Th>
            <Th>{t('settings.members.column.joined')}</Th>
            {canManageModelPermissions ? (
              <Th className="text-right">{t('settings.members.column.actions')}</Th>
            ) : null}
          </Tr>
        </THead>
        <TBody>
          {members.map((m) => (
            <Tr key={m.accountId} data-testid={`members-row-${m.accountId}`} interactive={false}>
              <Td mono heading>{m.email}</Td>
              <Td label={t('settings.members.column.role')}>
                <StatusChip
                  variant={m.role === 'owner' ? 'ok' : 'neutral'}
                  label={memberRoleLabel(t, m.role)}
                />
              </Td>
              <Td mono label={t('settings.members.column.joined')}>
                {formatTimestamp(m.joinedAt)}
              </Td>
              {canManageModelPermissions ? (
                <Td className="text-right" label={t('settings.members.column.actions')}>
                  {m.role === 'member' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEditModelPermissions(m)}
                      data-testid={`members-model-permissions-${m.accountId}`}
                    >
                      {t('settings.members.model_permissions')}
                    </Button>
                  ) : (
                    <span className="font-mono text-mono-label text-ink-low">
                      {t('settings.members.unrestricted')}
                    </span>
                  )}
                </Td>
              ) : null}
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

interface InvitationsSectionProps {
  readonly orgId: string | undefined;
  readonly query: ReturnType<typeof useOrgInvitations>;
}

function InvitationsSection({ orgId, query }: InvitationsSectionProps): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-3" data-testid="invitations-section">
      <header>
        <h3 className="m-0 text-label text-ink-mid">{t('settings.invitations.title')}</h3>
      </header>
      {query.isLoading ? (
        <Skeleton className="h-10" />
      ) : query.isError ? (
        <p className="m-0 text-body text-bad" role="alert" data-testid="invitations-error">
          {query.error instanceof PlatformApiError
            ? `${query.error.code}: ${query.error.message}`
            : t('settings.invitations.error')}
        </p>
      ) : query.data !== undefined ? (
        query.data.invitations.length === 0 ? (
          <p className="m-0 text-body text-ink-low" data-testid="invitations-empty">
            {t('settings.invitations.empty')}
          </p>
        ) : (
          <InvitationsTable orgId={orgId ?? ''} invitations={query.data.invitations} />
        )
      ) : null}
    </section>
  );
}

interface InvitationsTableProps {
  readonly orgId: string;
  readonly invitations: readonly OrgInvitationSummary[];
}

function InvitationsTable({ orgId, invitations }: InvitationsTableProps): JSX.Element {
  const t = useT();
  const revokeM = useRevokeOrgInvitation();
  const toast = useToast();
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  async function handleRevoke(invitationId: string): Promise<void> {
    setRowError(null);
    try {
      await revokeM.mutateAsync({ orgId, invitationId });
    } catch (err) {
      const message =
        err instanceof PlatformApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setRowError({ id: invitationId, message });
      toast.error(message);
    }
  }

  return (
    <div data-testid="invitations-table">
      <Table>
        <THead>
          <Tr interactive={false}>
            <Th>{t('settings.invitations.column.email')}</Th>
            <Th>{t('settings.invitations.column.role')}</Th>
            <Th>{t('settings.invitations.column.invited_by')}</Th>
            <Th>{t('settings.invitations.column.expires')}</Th>
            <Th className="text-right">{t('settings.invitations.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {invitations.map((inv) => (
            <Tr key={inv.id} data-testid={`invitations-row-${inv.id}`} interactive={false}>
              <Td mono heading>{inv.email}</Td>
              <Td label={t('settings.invitations.column.role')}>{invitedRoleLabel(t, inv.role)}</Td>
              <Td mono label={t('settings.invitations.column.invited_by')}>
                {inv.invitedByEmail ?? inv.invitedByAccountId}
              </Td>
              <Td mono label={t('settings.invitations.column.expires')}>
                {formatTimestamp(inv.expiresAt)}
              </Td>
              <Td className="text-right" label={t('settings.invitations.column.actions')}>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      void handleRevoke(inv.id);
                    }}
                    disabled={revokeM.isPending}
                    data-testid={`invitations-revoke-${inv.id}`}
                  >
                    {t('settings.invitations.revoke')}
                  </Button>
                </div>
                {rowError !== null && rowError.id === inv.id ? (
                  <p
                    className="m-0 mt-1 text-label text-bad"
                    role="alert"
                    data-testid={`invitations-revoke-error-${inv.id}`}
                  >
                    {rowError.message}
                  </p>
                ) : null}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

interface InviteMemberModalProps {
  readonly orgId: string;
  readonly onClose: () => void;
  readonly onCreated: (inviteUrl: string) => void;
}

function InviteMemberModal({ orgId, onClose, onCreated }: InviteMemberModalProps): JSX.Element {
  const t = useT();
  const createM = useCreateOrgInvitation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgInvitationRole>('member');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setError(t('settings.invite_modal.email_required'));
      return;
    }
    try {
      const res = await createM.mutateAsync({ orgId, body: { email: trimmed, role } });
      toast.success(t('settings.invite_modal.toast.sent'));
      onCreated(res.inviteUrl);
    } catch (err) {
      const message =
        err instanceof PlatformApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!createM.isPending) onClose();
      }}
      titleId="invite-member-modal-title"
      testId="members-invite-modal"
    >
      <form className="flex flex-col gap-5 p-6 sm:p-8" onSubmit={handleSubmit} noValidate>
        <div className="flex flex-col gap-1">
          <h2 id="invite-member-modal-title" className="m-0 font-display text-title text-ink">
            {t('settings.invite_modal.title')}
          </h2>
          <p className="m-0 text-caption text-ink-low">{t('settings.invite_modal.description')}</p>
        </div>
        <FormField label={t('settings.invite_modal.email')} fieldId="members-invite-email">
          <Input
            id="members-invite-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={createM.isPending}
            required
            maxLength={254}
            data-testid="members-invite-email"
          />
        </FormField>
        <FormField label={t('settings.invite_modal.role')} fieldId="members-invite-role">
          <select
            id="members-invite-role"
            value={role}
            onChange={(e) => {
              const nextRole = e.currentTarget.value;
              if (isOrgInvitationRole(nextRole)) {
                setRole(nextRole);
              }
            }}
            disabled={createM.isPending}
            data-testid="members-invite-role"
            className="h-9 w-full rounded border border-line bg-raised px-3 text-body text-ink transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60"
          >
            <option value="member">{t('settings.invite_modal.role.member')}</option>
            <option value="admin">{t('settings.invite_modal.role.admin')}</option>
          </select>
        </FormField>
        {error !== null ? (
          <p className="m-0 text-label text-bad" role="alert" data-testid="members-invite-error">
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={createM.isPending}
            data-testid="members-invite-cancel"
          >
            {t('settings.invite_modal.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createM.isPending}
            data-testid="members-invite-submit"
          >
            {createM.isPending
              ? t('settings.invite_modal.submitting')
              : t('settings.invite_modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface InviteLinkModalProps {
  readonly url: string;
  readonly onClose: () => void;
}

function InviteLinkModal({ url, onClose }: InviteLinkModalProps): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  return (
    <Modal isOpen onClose={onClose} titleId="invite-link-modal-title" testId="members-invite-link-modal">
      <div className="flex flex-col gap-5 p-6 sm:p-8">
        <div className="flex flex-col gap-1">
          <h2 id="invite-link-modal-title" className="m-0 font-display text-title text-ink">
            {t('settings.invite_link.title')}
          </h2>
          <p className="m-0 text-caption text-ink-low">{t('settings.invite_link.description')}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            void copy(url);
          }}
          data-testid="members-invite-link-copy"
          aria-label={t('settings.invite_link.aria_label')}
          className="group flex flex-col items-start gap-2 rounded-lg border border-line bg-raised p-4 text-left transition-colors duration-150 hover:bg-active/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        >
          <span className="font-mono text-mono-label text-ink-low">
            {t('settings.invite_link.click_to_copy')}
          </span>
          <span
            data-testid="members-invite-link-value"
            className="w-full select-all break-all font-mono text-body text-forest"
          >
            {url}
          </span>
          <span
            data-testid="members-invite-link-copy-status"
            className={cx('font-mono text-mono-label', copied ? 'text-ok' : 'text-ink-low')}
          >
            {copied ? t('settings.invite_link.copied') : t('settings.invite_link.copy_anywhere')}
          </span>
        </button>
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="members-invite-link-copy-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <Button variant="primary" onClick={onClose} data-testid="members-invite-link-done">
            {t('settings.invite_link.done')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
